import { useUserPrice } from '@ifixit/app/hooks';
import type { Money } from '@ifixit/helpers';
import { useTranslations } from '@ifixit/i18n/hooks';
import { useStoreLocale } from '@ifixit/i18n/hooks';
import { cn } from '@ifixit/ui/lib/utils';
import { type ComponentProps } from 'react';
import { Badge } from './badge';
import { FaIcon } from './icon';

export interface PriceTagProps extends Omit<ComponentProps<'div'>, 'children'> {
   price: Money;
   compareAtPrice?: Money | null;
   proPricesByTier?: Record<string, Money> | null;
   storeCode: string;
   /** Whether to show the discount percentage */
   showDiscountPercentage?: boolean;
}

/**
 * A flexible price display component that handles regular and discounted pricing with proper formatting.
 *
 * @remarks
 * Available CSS utility classes:
 * - Size variants: `price-tag-xs`, `price-tag-sm`, `price-tag-md` (default), `price-tag-lg`
 * - Layout variants: `price-tag-row` (default), `price-tag-column`, `price-tag-column-reverse`
 * - Style variants: `price-tag-normal` (default), `price-tag-subdued`
 */
export function PriceTag({
   price,
   compareAtPrice,
   proPricesByTier,
   storeCode,
   showDiscountPercentage = true,
   className,
   ...props
}: PriceTagProps) {
   const locale = useStoreLocale();
   const t = useTranslations();
   const { isProPrice, ...userPrice } = useUserPrice({
      price,
      compareAtPrice,
      proPricesByTier,
      storeCode,
   });

   const isDiscounted =
      userPrice.compareAtPrice != null &&
      Number(userPrice.compareAtPrice.amount) > Number(userPrice.price.amount);
   const discountPercentage =
      isDiscounted && userPrice.compareAtPrice != null
         ? calculateDiscountPercentage(userPrice.price, userPrice.compareAtPrice)
         : 0;

   const formattedPrice = formatPrice(userPrice.price, locale);
   const formattedCompareAtPrice = userPrice.compareAtPrice
      ? formatPrice(userPrice.compareAtPrice, locale)
      : undefined;

   return (
      <div data-slot="price-tag" className={cn(className)} {...props}>
         <div
            data-slot="price"
            data-testid="price"
            className={cn(
               'font-semibold leading-tight relative inline-flex items-center justify-center gap-1.5',
               isDiscounted ? (isProPrice ? 'text-amber-600' : 'text-red-600') : ''
            )}
         >
            {isProPrice && isDiscounted && (
               <FaIcon name="rectangle-pro" className="text-amber-500" />
            )}
            {isDiscounted && <span className="sr-only">{t('components.PriceTag.salePrice')} </span>}
            <span>{formattedPrice}</span>
         </div>

         {isDiscounted && formattedCompareAtPrice && (
            <>
               <span
                  data-slot="compare-price"
                  data-testid="compare-at-price"
                  className="text-gray-500 line-through"
               >
                  <span className="sr-only">{t('components.PriceTag.regularPrice')} </span>
                  {formattedCompareAtPrice}
               </span>

               {showDiscountPercentage && (
                  <Badge
                     className={cn(isProPrice ? 'badge-amber' : 'badge-red')}
                     data-testid="product-discount"
                  >
                     {t('components.badges.DiscountBadge.label', {
                        percentage: discountPercentage,
                     })}
                  </Badge>
               )}
            </>
         )}
      </div>
   );
}

function formatPrice(price: Money, locale: string): string {
   return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: price.currencyCode,
   }).format(Number(price.amount));
}

function calculateDiscountPercentage(price: Money, compareAtPrice: Money): number {
   const priceAmount = Number(price.amount);
   const compareAtPriceAmount = Number(compareAtPrice.amount);
   if (compareAtPriceAmount <= priceAmount) return 0;
   return Math.round(((compareAtPriceAmount - priceAmount) / compareAtPriceAmount) * 100);
}
